import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import ContentBlock from '@/components-latest/primitives/ContentBlock'
import MoreOptions, {
  MoreOptionsItem
} from '@/components-latest/primitives/MoreOptions'
import Separator from '@/components-latest/primitives/Separator'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import DisableConfirmation from '@/components-latest/ui/OfferPage/DisableConfirmation'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  useDeleteOfferBuyXGetYMutation,
  useUpdateBuyXGetYMutation
} from '@/ducks/offers/api/offers.api'
import { OfferBuyXGetY } from '@/ducks/offers/offers.types'
import { getRemainingTimeLeft } from '@/utils/time.utils'

import Styles from './BuyXGetYItem.module.scss'

const DISCOUNT_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2
}

type Props = {
  isLoading?: boolean
  offer: OfferBuyXGetY
  onEdit?: (itemId: string) => void
  onDelete?: (itemId: string) => void
}

const BuyXGetYItem: React.FC<Props> = ({ isLoading, offer, onEdit }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [deactivateModalIsOpen, setDeactivateModalIsOpen] = useState(false)
  const [deleteOfferBuyXGetY] = useDeleteOfferBuyXGetYMutation()
  const [deleteOfferModalIsOpen, setDeleteOfferModalIsOpen] = useState(false)
  const [updateBuyXGetY] = useUpdateBuyXGetYMutation()
  const updateOffer = async (offerId: string, status: number) => {
    try {
      const result = await updateBuyXGetY({
        status:
          status === 1 ? DISCOUNT_STATUS.INACTIVE : DISCOUNT_STATUS.ACTIVE,
        offerId: offerId
      }).unwrap()
      if (result?.message === 'success') {
        dispatch(
          addSystemNotification({
            message:
              status === 1
                ? t('offers.buyX-getY.more-actions.deactivate-offer.success')
                : t('offers.buyX-getY.more-actions.activate-offer.success'),
            intent: 'success'
          })
        )
      }
    } catch (error) {
      dispatch(
        addSystemNotification({
          message: t('offers.buyX-getY.more-actions.success'),
          intent: 'error'
        })
      )
    }
  }

  const handleDeactivateOffer = () => {
    if (!offer) return
    updateOffer(offer.id, offer.status)
    setDeactivateModalIsOpen(false)
  }

  const deleteOffer = async (offerId: string) => {
    try {
      const result = await deleteOfferBuyXGetY(offerId).unwrap()
      if (result?.status === '1') {
        dispatch(
          addSystemNotification({
            message: t('offers.buyX-getY.more-actions.delete-offer.success'),
            intent: 'success'
          })
        )
      }
    } catch (error) {
      dispatch(
        addSystemNotification({
          message: t('offers.buyX-getY.more-actions.delete-offer.error'),
          intent: 'error'
        })
      )
    }
  }

  return (
    <ContentBlock className={Styles.wrapper}>
      {isLoading ? (
        <div className={Styles.spinner} data-testid="loading-spinner-cart">
          <Spinner />
        </div>
      ) : offer ? (
        <>
          <div className={Styles.header}>
            <div className={Styles.headerLeft}>
              <Text variant="title-1">
                Buy {offer.itemsPurchased} Get {offer.itemsOffer}
              </Text>
              <span
                className={`${Styles.activeStatus} ${
                  offer.status === DISCOUNT_STATUS.ACTIVE
                    ? Styles.active
                    : Styles.inactive
                }`}>
                {offer.status === DISCOUNT_STATUS.ACTIVE
                  ? 'Active'
                  : 'Inactive'}
              </span>
            </div>
            <MoreOptions
              popoverClassName={Styles.optionsContainer}
              align="start"
              noArrow>
              <MoreOptionsItem
                id={offer.id}
                onClick={() => onEdit?.(offer.id)}
                className={Styles.option}>
                <Text variant="nextSubtitle">{t('offers.edit-offer')}</Text>
              </MoreOptionsItem>
              <MoreOptionsItem
                id={offer.id}
                onClick={() => setDeactivateModalIsOpen(true)}
                className={Styles.option}>
                <Text variant="nextSubtitle">
                  {offer.status === 1
                    ? t(
                        'offers.cart-discount.more-actions.deactivate-offer.title'
                      )
                    : t(
                        'offers.cart-discount.more-actions.activate-offer.title'
                      )}
                </Text>
              </MoreOptionsItem>
              <MoreOptionsItem
                id={offer.id}
                onClick={() => setDeleteOfferModalIsOpen(true)}
                className={Styles.option}>
                <Text variant="nextSubtitle">{t('offers.delete-offer')}</Text>
              </MoreOptionsItem>
            </MoreOptions>
          </div>

          <Separator variant="solid" />

          <div className={Styles.detailsSection}>
            <div className={Styles.detailItem}>
              <Text variant="subtitle">{t('offers.buyX-getY.buy')}</Text>
              <Text variant="subtitle">
                {offer.itemsPurchased}* {offer.item1.name}
              </Text>
            </div>

            <Separator variant="squared" className={Styles.separator} />
            <div className={Styles.detailItem}>
              <Text variant="subtitle">{t('offers.buyX-getY.get')}</Text>
              <Text variant="subtitle">
                {offer.itemsOffer}* {offer.item2[0].name}
              </Text>
            </div>

            <Separator variant="squared" className={Styles.separator} />
            <div className={Styles.detailItem}>
              <Text variant="subtitle">
                {t('offers.buyX-getY.discount-value')}
              </Text>
              <Text variant="subtitle">
                {offer.item2[0].discountPercent}% off
              </Text>
            </div>

            <Separator variant="squared" className={Styles.separator} />
            <div className={Styles.detailItem}>
              <Text variant="subtitle">{t('offers.buyX-getY.cumulative')}</Text>
              <Text variant="subtitle">
                {offer.isCumulative ? 'Yes' : 'No'}
              </Text>
            </div>

            <Separator variant="squared" className={Styles.separator} />
            <div className={Styles.detailItem}>
              <Text variant="subtitle">{t('offers.buyX-getY.expires-in')}</Text>
              <Text variant="subtitle">
                {getRemainingTimeLeft(offer.expiryDate)}
              </Text>
            </div>
          </div>

          <Separator variant="squared" className={Styles.separator} />
        </>
      ) : (
        <Text variant="nextSubtitle">{t('offers.no-data.title')}</Text>
      )}
      <DisableConfirmation
        open={deleteOfferModalIsOpen}
        onOpenChange={setDeleteOfferModalIsOpen}
        onConfirm={() => deleteOffer(offer.id)}
        title={t('offers.buyX-getY.more-actions.delete-offer.title')}
        description={t(
          'offers.buyX-getY.more-actions.delete-offer.description'
        )}
        cancelText={t('common.modal.cancel')}
        confirmText={t('common.modal.confirm')}
      />
      <DisableConfirmation
        open={deactivateModalIsOpen}
        onOpenChange={setDeactivateModalIsOpen}
        onConfirm={handleDeactivateOffer}
        title={
          offer.status === 1
            ? t('offers.buyX-getY.more-actions.deactivate-offer.title')
            : t('offers.buyX-getY.more-actions.activate-offer.title')
        }
        description={
          offer.status === 1
            ? t('offers.buyX-getY.more-actions.deactivate-offer.description')
            : t('offers.buyX-getY.more-actions.activate-offer.description')
        }
        cancelText={t('common.modal.cancel')}
        confirmText={t('common.modal.confirm')}
      />
    </ContentBlock>
  )
}

BuyXGetYItem.displayName = 'BuyXGetYItem'
export default BuyXGetYItem
